/**
 * Collapsible accordion with per-country gateway/campaign/TLD overrides.
 * Also shows propagated countries so they can be configured.
 */

import { useState } from 'react';
import { ChevronDown, Flag } from 'lucide-react';

import { SearchableSelect } from '@/components/ui';
import { ModalSection } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji, countryKeyToIso2 } from '@/utils/country-flag-emoji';

import type { PerCountryOverride } from '../shared';

interface CatalogForCountry {
  gateways: Array<{ id: number; name: string; pricePerSms?: number }>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: Array<{ value: string; displayName: string }>;
  leadsLists?: Array<{ id: number; name: string }>;
}

interface Props {
  countryKeys: string[];
  overrides: Record<string, PerCountryOverride>;
  onOverride: (ck: string, patch: Partial<PerCountryOverride>) => void;
  catalogByCountry: Record<string, CatalogForCountry>;
  propagatedCountries?: string[];
  disabled?: boolean;
}

export function PerCountryOverridesPanel(props: Props) {
  const { countryKeys, overrides, onOverride, catalogByCountry, propagatedCountries = [], disabled } = props;
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggle = (ck: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(ck)) next.delete(ck); else next.add(ck);
      return next;
    });
  };

  if (countryKeys.length <= 1) return null;
  const propagatedSet = new Set(propagatedCountries);

  return (
    <ModalSection
      title="Per-Country Overrides"
      icon={<Flag className="h-3.5 w-3.5" />}
      badge={<span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">{countryKeys.length}</span>}
    >
      <div className="space-y-1">
        {countryKeys.map((ck) => (
          <CountryAccordion
            key={ck} countryKey={ck}
            expanded={expandedKeys.has(ck)} onToggle={() => toggle(ck)}
            override={overrides[ck] ?? {}} onChange={(patch) => onOverride(ck, patch)}
            catalog={catalogByCountry[ck]} disabled={disabled}
            isPropagated={propagatedSet.has(ck)}
          />
        ))}
      </div>
    </ModalSection>
  );
}

function CountryAccordion(props: {
  countryKey: string;
  expanded: boolean;
  onToggle: () => void;
  override: PerCountryOverride;
  onChange: (patch: Partial<PerCountryOverride>) => void;
  catalog?: CatalogForCountry;
  disabled?: boolean;
  isPropagated?: boolean;
}) {
  const { countryKey, expanded, onToggle, override, onChange, catalog, disabled, isPropagated } = props;
  const hasOverride = override.routeId != null || override.wiserId != null || override.shortLinkTldValue != null;
  const iso = countryKeyToIso2(countryKey) || '—';

  return (
    <div className={cn(
      'rounded-md border',
      hasOverride ? 'border-primary/40 bg-primary/5' : isPropagated ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/60',
    )}>
      <button type="button" className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left" onClick={onToggle} disabled={disabled}>
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-lg leading-none" aria-hidden>
            {countryToFlagEmoji(countryKey)}
          </span>
          <span className="flex min-w-0 items-baseline gap-1.5 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold">
            <span className="shrink-0 text-muted-foreground">{iso}</span>
            <span className="min-w-0 truncate capitalize">{countryKey.replace(/_/g, ' ')}</span>
          </span>
          {hasOverride && <span className="text-[10px] text-primary">overridden</span>}
          {isPropagated && !hasOverride && <span className="text-[10px] text-amber-600">propagated</span>}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && catalog && (
        <div className="space-y-2 border-t border-border/40 p-3">
          <OverrideField
            label="Gateway"
            options={catalog.gateways.map((g) => ({
              value: g.id,
              label: `${g.name}${
                g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''
              }`,
            }))}
            value={override.routeId}
            onChange={(v) => onChange({ routeId: v })}
          />
          <OverrideField
            label="Campaign" options={catalog.campaigns.map((c) => ({ value: c.id, label: c.name }))}
            value={override.wiserId} onChange={(v) => onChange({ wiserId: v })}
          />
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">TLD override</label>
            <SearchableSelect<string>
              options={[{ value: '', label: '— Use shared —' }, ...catalog.tlds.map((t) => ({ value: t.value, label: t.displayName }))]}
              value={override.shortLinkTldValue ?? ''} onChange={(v) => onChange({ shortLinkTldValue: v || undefined })}
              placeholder="Use shared"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function OverrideField(props: {
  label: string;
  options: Array<{ value: number; label: string }>;
  value?: number;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{props.label}</label>
      <SearchableSelect<number>
        options={[{ value: -1, label: '— Use shared —' }, ...props.options]}
        value={props.value ?? -1} onChange={(v) => props.onChange(v === -1 ? undefined : v)}
        placeholder={`Select ${props.label.toLowerCase()}`}
      />
    </div>
  );
}
